import { createContext, useContext, useMemo } from "react";

type AnalyticsContextProviderProps = {
  properties: Record<string, unknown>;
};

type AnalyticsContextProviderState = {
  properties: Record<string, unknown>;
};

export const AnalyticsContext = createContext<AnalyticsContextProviderState>({
  properties: {},
});

export const AnalyticsContextProvider = ({
  properties,
  children,
}: React.PropsWithChildren<AnalyticsContextProviderProps>) => {
  const parentProperties = useContext(AnalyticsContext).properties;

  // Serialize as a stable cache key so a new object reference with
  // identical contents doesn't invalidate the memo.
  const propertiesKey = JSON.stringify(properties);

  const state = useMemo(
    () => ({
      properties: {
        ...parentProperties,
        ...(JSON.parse(propertiesKey) as Record<string, unknown>),
      },
    }),
    [parentProperties, propertiesKey],
  );

  return (
    <AnalyticsContext.Provider value={state}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  return useContext(AnalyticsContext).properties;
};
